// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\BusinessPromocodes.jsx
// Название: Business Promocodes Management v5.0
// Версия: 1.0.0
// Дата: 21.01.2026

import React, { useState, useEffect } from 'react';
import './BusinessPromocodes.css';

const API_URL = 'https://qr-money.onrender.com/api';

const BusinessPromocodes = () => {
  const [promocodes, setPromocodes] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState({ total: 0, available: 0, assigned: 0, redeemed: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ campaign_id: '', status: '' });
  const [showGenerate, setShowGenerate] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [generateForm, setGenerateForm] = useState({ campaign_id: '', prefix: '', count: 50, type: 'main' });
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadData();
  }, [filter.campaign_id, filter.status]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('authToken');

      // Кампании
      const campRes = await fetch(`${API_URL}/campaigns`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const campData = await campRes.json();
      if (campData.success) {
        setCampaigns(campData.data || []);
      }

      // Промокоды
      if (filter.campaign_id) {
        const promoRes = await fetch(`${API_URL}/promocodes/campaign/${filter.campaign_id}?status=${filter.status}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const promoData = await promoRes.json();
        if (promoData.success) {
          setPromocodes(promoData.data.promocodes || []);
          setStats(promoData.data.stats || { total: 0, available: 0, assigned: 0, redeemed: 0 });
        }
      } else {
        setPromocodes([]);
        setStats({ total: 0, available: 0, assigned: 0, redeemed: 0 });
      }
    } catch (error) {
      console.error('Load data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!generateForm.campaign_id || !generateForm.count) return;
    
    setGenerating(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/promocodes/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(generateForm)
      });
      const data = await res.json();
      
      if (data.success) {
        alert(`✅ Сгенерировано ${data.data.generated} промокодов`);
        setShowGenerate(false);
        setFilter({ ...filter, campaign_id: generateForm.campaign_id });
        loadData();
      } else {
        alert('❌ Ошибка: ' + data.error);
      }
    } catch (error) {
      alert('❌ Ошибка генерации');
    } finally {
      setGenerating(false);
    }
  };

  const handleUploadCSV = async (e) => {
    const file = e.target.files[0];
    if (!file || !filter.campaign_id) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      const promocodes = lines.slice(1).map(line => {
        const [code, type, dar_amount] = line.split(',').map(s => s.trim());
        return { code, type: type || 'main', dar_amount: parseFloat(dar_amount) || 10 };
      }).filter(p => p.code);

      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_URL}/promocodes/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ campaign_id: filter.campaign_id, promocodes })
        });
        const data = await res.json();

        if (data.success) {
          alert(`✅ Импортировано: ${data.data.imported}, пропущено: ${data.data.skipped}`);
          loadData();
        } else {
          alert('❌ Ошибка: ' + data.error);
        }
      } catch (error) {
        alert('❌ Ошибка загрузки');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('ru-RU', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      available: { class: 'green', label: '✓ Доступен' },
      assigned: { class: 'yellow', label: '⏳ Назначен' },
      redeemed: { class: 'purple', label: '✓ Использован' },
      expired: { class: 'gray', label: '✕ Истёк' }
    };
    const b = badges[status] || badges.available;
    return <span className={`bp-badge bp-badge-${b.class}`}>{b.label}</span>;
  };

  if (loading) {
    return <div className="bp-loading"><div className="bp-spinner"></div></div>;
  }

  return (
    <div className="bp-page">
      <div className="bp-header">
        <div className="bp-header-left">
          <h1>🎟️ Промокоды</h1>
          <p>Управление промокодами для кампаний</p>
        </div>
        <div className="bp-header-actions">
          <button className="bp-btn bp-btn-secondary" onClick={() => setShowGenerate(true)}>
            ➕ Генерировать
          </button>
          <label className="bp-btn bp-btn-secondary">
            📤 Загрузить CSV
            <input type="file" accept=".csv" onChange={handleUploadCSV} hidden disabled={!filter.campaign_id} />
          </label>
        </div>
      </div>

      {/* Выбор кампании */}
      <div className="bp-campaign-select">
        <label>Выберите кампанию:</label>
        <select
          value={filter.campaign_id}
          onChange={(e) => setFilter({ ...filter, campaign_id: e.target.value })}
          className="bp-select-large"
        >
          <option value="">— Выберите кампанию —</option>
          {campaigns.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {filter.campaign_id ? (
        <>
          {/* Статистика */}
          <div className="bp-stats">
            <div className="bp-stat">
              <span className="bp-stat-num">{stats.total}</span>
              <span className="bp-stat-label">Всего</span>
            </div>
            <div className="bp-stat">
              <span className="bp-stat-num bp-green">{stats.available}</span>
              <span className="bp-stat-label">Доступно</span>
            </div>
            <div className="bp-stat">
              <span className="bp-stat-num bp-yellow">{stats.assigned}</span>
              <span className="bp-stat-label">Назначено</span>
            </div>
            <div className="bp-stat">
              <span className="bp-stat-num bp-purple">{stats.redeemed}</span>
              <span className="bp-stat-label">Использовано</span>
            </div>
          </div>

          {/* Фильтр по статусу */}
          <div className="bp-filters">
            <select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className="bp-select"
            >
              <option value="">Все статусы</option>
              <option value="available">Доступные</option>
              <option value="assigned">Назначенные</option>
              <option value="redeemed">Использованные</option>
            </select>
          </div>

          {/* Таблица */}
          {promocodes.length === 0 ? (
            <div className="bp-empty">
              <div className="bp-empty-icon">🎫</div>
              <h3>Нет промокодов</h3>
              <p>Сгенерируйте или загрузите промокоды для этой кампании</p>
            </div>
          ) : (
            <div className="bp-table-wrapper">
              <table className="bp-table">
                <thead>
                  <tr>
                    <th>Код</th>
                    <th>Тип</th>
                    <th>DAR</th>
                    <th>Статус</th>
                    <th>Кому выдан</th>
                    <th>Дата выдачи</th>
                  </tr>
                </thead>
                <tbody>
                  {promocodes.map((p, i) => (
                    <tr key={i}>
                      <td className="bp-code">{p.code}</td>
                      <td>{p.type === 'main' ? '🎁 Прямой' : '👥 Реферал'}</td>
                      <td>{p.dar_amount} DAR</td>
                      <td>{getStatusBadge(p.status)}</td>
                      <td>{p.assigned_email || '—'}</td>
                      <td className="bp-date">{formatDate(p.redeemed_at || p.assigned_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <div className="bp-empty">
          <div className="bp-empty-icon">👆</div>
          <h3>Выберите кампанию</h3>
          <p>Для просмотра и управления промокодами выберите кампанию выше</p>
        </div>
      )}

      {/* Модалка генерации */}
      {showGenerate && (
        <div className="bp-modal-overlay" onClick={() => setShowGenerate(false)}>
          <div className="bp-modal" onClick={e => e.stopPropagation()}>
            <h2>➕ Генерация промокодов</h2>
            
            <div className="bp-form-group">
              <label>Кампания</label>
              <select
                value={generateForm.campaign_id}
                onChange={(e) => setGenerateForm({ ...generateForm, campaign_id: e.target.value })}
              >
                <option value="">Выберите</option>
                {campaigns.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="bp-form-group">
              <label>Префикс кода</label>
              <input
                type="text"
                placeholder="NIKE"
                value={generateForm.prefix}
                onChange={(e) => setGenerateForm({ ...generateForm, prefix: e.target.value.toUpperCase() })}
                maxLength={10}
              />
            </div>

            <div className="bp-form-group">
              <label>Количество</label>
              <input
                type="number"
                value={generateForm.count}
                onChange={(e) => setGenerateForm({ ...generateForm, count: parseInt(e.target.value) || 0 })}
                min={1}
                max={1000}
              />
            </div>

            <div className="bp-form-group">
              <label>Тип</label>
              <select
                value={generateForm.type}
                onChange={(e) => setGenerateForm({ ...generateForm, type: e.target.value })}
              >
                <option value="main">Прямой (10 DAR)</option>
                <option value="referral">Реферал (5 DAR)</option>
              </select>
            </div>

            <div className="bp-modal-actions">
              <button className="bp-btn" onClick={() => setShowGenerate(false)}>Отмена</button>
              <button 
                className="bp-btn bp-btn-primary" 
                onClick={handleGenerate}
                disabled={generating || !generateForm.campaign_id}
              >
                {generating ? 'Генерация...' : 'Сгенерировать'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessPromocodes;
